/**
 * The sky (M3-05) — daylight, weather, and the wind that carries both.
 *
 * Everything in this file is **pure and derived from the tick or from a seeded
 * draw**. Nothing reads a clock, and the only state it owns is the small
 * `Weather` record that lives in world state: which way the sky is going, since
 * when, where the wind is coming from, and how wet the ground has got. A save
 * taken mid-rain at midnight comes back mid-rain at midnight because those four
 * numbers are in the snapshot and everything else is arithmetic over the tick.
 *
 * Three decisions worth stating, because each is easy to undo with a
 * well-meaning edit:
 *
 * - **Weather is a Markov chain rolled once a game hour**, not a scripted
 *   forecast and not a duration timer. One draw from `rng('weather')` per game
 *   hour against a transition matrix — the self-transition weight is what makes
 *   rain last for hours rather than flicker, so a "how long does it rain for"
 *   knob would be a second answer to a question the matrix already answers.
 * - **The matrix is biased for Wales.** Read down the `rain` row: the most
 *   likely thing after rain is more rain. That is not a balance accident, it is
 *   the place. Cwm Twrch is on the west coast and the weather comes up the
 *   valley off the sea.
 * - **Wetness is state, not a flag.** Rain raises it and dry hours lower it, so
 *   the ground stays wet for a while after the shower stops — which is what
 *   makes "wait for it to dry out before you try to light that" a real decision
 *   rather than a coin flip on the current sky.
 *
 * Wetness is stored in thousandths and never as a float, for the reason blood
 * is stored in milli-units: world state is hashed for determinism and a float
 * accumulated over ten thousand hours is a fingerprint that drifts.
 *
 * This module knows nothing about the world beyond its own record — the rules
 * that read it live in `sky.ts`, the same split `mind.ts`/`beasts.ts` makes and
 * for the same reason: `world.ts` has to name `Weather` in order to store it.
 */
import { DIRECTIONS } from './actor.js';
import { MINUTES_PER_GAME_HOUR, TICKS_PER_GAME_HOUR } from './time.js';
/* -------------------------------------------------------------------------- *
 * The light of day
 * -------------------------------------------------------------------------- */
/**
 * When the sun comes up and goes down, in game hours, and how long it takes.
 *
 * A game day is one real hour, so full dark runs from 22:00 to 04:00 — five
 * game hours either side of midnight, which is a little over twelve real
 * minutes. That is the ticket's number, and it is short in real terms on
 * purpose: night has to be something a session runs into rather than something
 * a session is.
 */
export const DAWN_START = 4;
export const DAWN_END = 6;
export const DUSK_START = 20;
export const DUSK_END = 22;
/**
 * How much sun is on the valley, 0 at midnight and 1 at noon.
 *
 * Linear through the dawn and dusk ramps rather than a smooth curve: the ramps
 * are two game hours — five real minutes — and a player crossing one should be
 * able to *feel* the light going, which a cosine does more gently than the
 * screen can show.
 */
export function daylight(time) {
    const hour = time.hour + time.minute / MINUTES_PER_GAME_HOUR;
    if (hour <= DAWN_START || hour >= DUSK_END)
        return 0;
    if (hour >= DAWN_END && hour <= DUSK_START)
        return 1;
    if (hour < DAWN_END)
        return (hour - DAWN_START) / (DAWN_END - DAWN_START);
    return (DUSK_END - hour) / (DUSK_END - DUSK_START);
}
/**
 * The tick a *fresh* valley opens on: eight in the morning.
 *
 * Tick 0 is a calendar origin, not a time anybody should be dropped into — and
 * since M3-05 it is emphatically midnight, which would mean every new server
 * greeted its first player with three tiles of sight and a wolf. The offset
 * lives here rather than in `createWorld` because it is a decision about how
 * the game *opens*, not about what a world is: the engine's own tests want a
 * world at tick 0, and a restored world brings its own tick.
 */
export const OPENING_TICK = TICKS_PER_GAME_HOUR * 8;
/** The four times of day a player names. What the tint pass and the log say. */
export const SKY_PHASES = ['night', 'dawn', 'day', 'dusk'];
export function skyPhase(time) {
    const hour = time.hour + time.minute / MINUTES_PER_GAME_HOUR;
    if (hour <= DAWN_START || hour >= DUSK_END)
        return 'night';
    if (hour < DAWN_END)
        return 'dawn';
    if (hour <= DUSK_START)
        return 'day';
    return 'dusk';
}
/** What the log says when the light turns. */
export function phaseText(phase) {
    switch (phase) {
        case 'dawn':
            return 'The sky greys in the east.';
        case 'day':
            return 'The sun is up over the valley.';
        case 'dusk':
            return 'The light is going off the hill.';
        default:
            return 'Night closes over Cwm Twrch.';
    }
}
/* -------------------------------------------------------------------------- *
 * Weather
 * -------------------------------------------------------------------------- */
/**
 * Every sky the valley knows. Appended to, never reordered — the ids are in the
 * archive (M0-07) and on the wire.
 */
export const WEATHER_KINDS = ['clear', 'overcast', 'rain', 'heavy-rain', 'fog', 'wind'];
/** Wetness as a fraction, for the rules that want one. */
export const MAX_WETNESS = 1000;
export function wetnessOf(weather) {
    return weather.wet / MAX_WETNESS;
}
/**
 * The prevailing wind. Cwm Twrch runs south-west to north-east and the weather
 * comes up it off the sea, which is why a hunter on the moor top is downwind of
 * everything and a hunter on the shore is announced.
 */
export const PREVAILING_WIND = 'sw';
export function createWeather() {
    return { kind: 'overcast', since: 0, wind: PREVAILING_WIND, wet: 200 };
}
/**
 * The transition matrix, as relative weights per game hour.
 *
 * Rows are the sky now, columns the sky an hour from now. The diagonal is
 * persistence — raise it and weather lasts longer; the off-diagonals are the
 * shape of a Welsh autumn. Nothing here is a probability: `pickWeighted`
 * normalises, so scaling a whole row changes nothing and scaling one cell
 * changes what follows what.
 */
export const WEATHER_MATRIX = {
    clear: { clear: 5, overcast: 4, rain: 1.5, 'heavy-rain': 0.1, fog: 0.6, wind: 1 },
    overcast: { clear: 2, overcast: 4, rain: 3.5, 'heavy-rain': 0.6, fog: 1, wind: 1.2 },
    rain: { clear: 0.6, overcast: 3, rain: 4, 'heavy-rain': 1.6, fog: 0.4, wind: 1 },
    'heavy-rain': { clear: 0.2, overcast: 1.5, rain: 4, 'heavy-rain': 2, fog: 0.2, wind: 1.2 },
    fog: { clear: 1.2, overcast: 2, rain: 1, 'heavy-rain': 0.2, fog: 3.5, wind: 0.6 },
    wind: { clear: 1.5, overcast: 2.5, rain: 2, 'heavy-rain': 0.8, fog: 0.2, wind: 3 },
};
/**
 * What each sky does to the ground per game hour, in thousandths.
 *
 * Note the asymmetry: heavy rain soaks the valley in three hours and it takes
 * most of a day of clear weather to dry out again. That is the shape of peat,
 * and it is what stops "wait for the rain to stop" from being a five-minute
 * answer to every fire.
 */
export const RAINFALL = {
    clear: -120,
    overcast: -40,
    rain: 180,
    'heavy-rain': 320,
    // Fog is water in the air rather than on the ground, but it does not dry
    // anything either.
    fog: 20,
    wind: -160,
};
/**
 * What each sky costs you in sight, as a multiplier on the radius.
 *
 * Fog is the one that changes how the valley is played: half your sight, in
 * daylight, for as long as it sits. `overcast` is 1 because *gloom is not
 * distance* — an overcast noon is darker to look at and you can still see the
 * far bank, and confusing the two is how a weather system ends up making every
 * sky a sight penalty.
 */
export const WEATHER_SIGHT = {
    clear: 1,
    overcast: 1,
    rain: 0.8,
    'heavy-rain': 0.65,
    fog: 0.5,
    wind: 0.95,
};
/**
 * How much the sky swallows the light, for the renderer's tint pass only.
 *
 * Deliberately *not* fed into the sight radius: gloom is a look, and doubling it
 * as a rule would count the same judgement twice — once in `WEATHER_SIGHT` and
 * once here.
 */
export const WEATHER_GLOOM = {
    clear: 0,
    overcast: 0.25,
    rain: 0.35,
    'heavy-rain': 0.5,
    fog: 0.45,
    wind: 0.15,
};
export function isRaining(kind) {
    return kind === 'rain' || kind === 'heavy-rain';
}
/* -------------------------------------------------------------------------- *
 * The hourly roll
 * -------------------------------------------------------------------------- */
/**
 * Chance the wind veers a point either way on the hour.
 *
 * Small, and it never jumps: the wind swings round over a morning rather than
 * reversing between one hour and the next, because a hunter who worked out
 * which way to approach has to be able to rely on the answer for longer than it
 * took them to walk there.
 */
export const WIND_VEER_CHANCE = 0.25;
/** One point clockwise or anticlockwise on the eight-point compass. */
export function veer(wind, clockwise) {
    const index = DIRECTIONS.indexOf(wind);
    const next = (index + (clockwise ? 1 : DIRECTIONS.length - 1)) % DIRECTIONS.length;
    return DIRECTIONS[next];
}
/**
 * Roll the sky forward one game hour, in place.
 *
 * Returns the sky it was, so the caller can narrate a change without keeping a
 * copy.
 *
 * The ground is soaked or dried by the sky that was in force over the hour that
 * has just *passed*, not by the one about to begin: an hour of clear weather
 * followed by a roll that brings rain dried the ground, and charging that hour
 * as rain would be weather taking effect before it arrived.
 *
 * The draw order — sky, then wind — is fixed and load-bearing: the stream
 * position after this call must depend only on state, or a replay could pick up
 * the dice half a word out.
 */
export function advanceWeather(weather, rng, tick) {
    const was = weather.kind;
    weather.wet = clampWet(weather.wet + RAINFALL[was]);
    const row = WEATHER_MATRIX[was];
    const next = rng.pickWeighted(WEATHER_KINDS, (kind) => row[kind]);
    if (next !== was) {
        weather.kind = next;
        weather.since = tick;
    }
    if (rng.chance(WIND_VEER_CHANCE)) {
        weather.wind = veer(weather.wind, rng.chance(0.5));
    }
    return was;
}
function clampWet(value) {
    return value < 0 ? 0 : value > MAX_WETNESS ? MAX_WETNESS : Math.round(value);
}
/* -------------------------------------------------------------------------- *
 * What the weather is worth to the rest of the game
 * -------------------------------------------------------------------------- */
/**
 * The best chance anybody has of getting a light going right now.
 *
 * Falling rain is the immediate problem and wet ground is the lingering one, so
 * both are charged — which is why lighting a brand ten minutes after a
 * downpour is still a gamble and lighting one during a downpour is nearly
 * hopeless. Never zero: the floor is the difference between "hard" and "the
 * game has taken the verb away from you".
 */
export const KINDLE_BASE = 0.92;
export const KINDLE_WETNESS_PENALTY = 0.6;
export const KINDLE_RAIN_PENALTY = 0.25;
export const KINDLE_FLOOR = 0.05;
export function kindleChance(weather, options = {}) {
    // A roof keeps the falling rain off the tinder (RC1-07) — the rain penalty is
    // the difference between building a fire under a stick hut and out in the open.
    // The ambient-wetness penalty stays: the air under a bothy in a wet week is
    // still damp, so cover helps most in an active downpour and least in a drizzle.
    const penalty = wetnessOf(weather) * KINDLE_WETNESS_PENALTY +
        (!options.sheltered && isRaining(weather.kind) ? KINDLE_RAIN_PENALTY : 0);
    return Math.max(KINDLE_FLOOR, KINDLE_BASE - penalty);
}
/**
 * How readily anything catches, 0–1 — M3-04's flammability, damped (M3-06 will
 * ask this of a fire that is already going, which is a different question from
 * whether one starts).
 */
export function flammability(weather) {
    return Math.max(0, 1 - wetnessOf(weather));
}
/**
 * The infection environment the valley is currently offering (M1-05).
 *
 * The constants `CLEAN_CONDITIONS` and `FOUL_CONDITIONS` were placeholders for
 * exactly this — a dry hearth is 1 and a soaking bog day is 2, and now the
 * number moves with the sky instead of being chosen by whoever called the
 * function.
 */
export function infectionEnvironmentOf(weather) {
    return { filth: 1 + wetnessOf(weather) };
}
/* -------------------------------------------------------------------------- *
 * Narration
 * -------------------------------------------------------------------------- */
/**
 * What the log says when the sky turns.
 *
 * Written as weather arriving rather than as a state being reported — "rain
 * sweeps in from the sea", not "weather: rain" — because this line is the only
 * thing that tells a player the valley has an opinion about the next hour.
 */
export function weatherText(to, wind) {
    switch (to) {
        case 'clear':
            return 'The cloud breaks up and goes off eastward.';
        case 'overcast':
            return 'The sky closes over, flat and grey.';
        case 'rain':
            return `Rain sweeps in from the ${quarter(wind)}.`;
        case 'heavy-rain':
            return 'The rain comes on hard enough to hurt.';
        case 'fog':
            return 'Fog comes up off the water and the far bank goes.';
        default:
            return `The wind gets up out of the ${quarter(wind)}.`;
    }
}
const QUARTERS = {
    n: 'north',
    ne: 'north-east',
    e: 'east',
    se: 'south-east',
    s: 'south',
    sw: 'south-west',
    w: 'west',
    nw: 'north-west',
};
/** A compass point, spelled the way a person says it. */
export function quarter(wind) {
    return QUARTERS[wind];
}
/** `"rain"`, `"heavy rain"` — the id with its hyphen taken out, for prose. */
export function weatherName(kind) {
    return kind.replace('-', ' ');
}
/**
 * How many game hours a sky has been sitting. For the log and for M3-08, which
 * wants to know whether the sign it is reading has been rained on since.
 */
export function hoursOfWeather(weather, tick) {
    return Math.floor((tick - weather.since) / TICKS_PER_GAME_HOUR);
}
//# sourceMappingURL=weather.js.map